import dayjs from 'dayjs';
import { useState } from 'react';
import React from 'react';

import { useSession } from '@hooks/useSession';

import { formatName } from '@utils/format';

import {
  useListAuditLogs,
  useListAuditLogUsers,
} from 'generated/audit_logs.generated';
import type { AuditLogFilterInput } from 'generated/types';

export interface UseAuditLogsOptions {
  initialFilters?: AuditLogFilterInput;
  initialPageSize?: number;
}

export const useAuditLogs = ({
  initialFilters,
  initialPageSize = 25,
}: UseAuditLogsOptions) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [filters, setFilters] = useState<AuditLogFilterInput>(
    initialFilters || {}
  );

  const {
    state: { activeProfile },
  } = useSession();

  const vitall_patient_id = activeProfile?.vitall_patient_id ?? '';

  const { data, loading } = useListAuditLogs({
    variables: {
      input: {
        count: pageSize,
        page,
        filters: {
          vitall_patient_id: vitall_patient_id,
          eventTypes: filters.eventTypes ?? null,
          userIds: filters.userIds ?? null,
          startDate: filters.startDate
            ? dayjs(filters.startDate).utc().toISOString()
            : null,
          endDate: filters.endDate
            ? dayjs(filters.endDate).utc().toISOString()
            : null,
        },
      },
    },
    skip: !(activeProfile && vitall_patient_id),

    fetchPolicy: 'cache-and-network',
  });

  const auditLogsResponse = data?.listAuditLogs ?? null;

  return {
    auditLogs: auditLogsResponse?.data ?? [],
    total: auditLogsResponse?.total ?? 0,
    loading,
    page,
    setPage,
    pageSize,
    setPageSize,
    filters,
    setFilters,
  };
};

export const useAuditLogUserFilter = () => {
  const {
    state: { activeProfile },
  } = useSession();

  const vitall_patient_id = activeProfile?.vitall_patient_id ?? '';

  const { data } = useListAuditLogUsers({
    variables: {
      input: {
        filters: { vitall_patient_id, onlyUsers: true },
      },
    },
    skip: !(activeProfile && vitall_patient_id),
  });

  const uniqueUsers = React.useMemo(() => {
    if (!data?.listAuditLogs.data) return [];

    const map = new Map<
      string,
      { user_id: string; first_name: string; last_name: string }
    >();

    for (const log of data.listAuditLogs.data) {
      if (log.user?.user_id) {
        map.set(log.user.user_id, log.user);
      }
    }

    return Array.from(map.values()).map((u) => ({
      fullName: formatName(u.first_name, u.last_name),
      userId: u.user_id,
    }));
  }, [data]);

  return { uniqueUsers };
};
